import React, { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Container,
  Row,
  Col,
  Table,
  Card as BSCard,
  CardBody,
} from "reactstrap";
import Breadcrumb from "../common/breadcrumb";
import { deviceAction, fetchBranches, fetchDevices, fetchDeviceReduxState, fetchOrderStatsByDate, fetchNetworkDiagnostics, fetchErrorLogs, getFloors, fetchRoutableChannels, getDeliverectVirtualLocations } from "../../utils/apiCalls";
import { RefreshCw, Wifi, WifiOff, HardDrive, ShoppingCart, AlertTriangle, FileText, Eye, LogIn, ChevronDown, ChevronRight, Layers, List as ListIcon } from "react-feather";
import CountUp from "react-countup";
import { useSelector } from "react-redux";
import { C, tbl, reportingGlobalCSS } from "../reporting/reportingStyles";
import MultiSelectField from "../common/MultiSelectField";
import { Tile, RCard, Loader as RLoader } from "../reporting/ReportingShared";
import DeviceDetailsModal from "./DeviceDetailsModal";
import { branchIdOf, deviceLabel, formatChannelNames, isReceiving, routingClaimsOf, summarizeBranches } from "./deviceRouting";
import { formatCustomDateTime, getTimeAgo, StatusPill, RoutingModeBadge, ChannelChip, KeyStatusPill } from "./deviceUi";

// Columns whose value is not a plain field on the row.
const SORT_ACCESSORS = {
  staleAgeDays: (d) => d.staleOrdersAlert?.maxAgeDays ?? 0,
};

const GROUP_BY_BRANCH_KEY = "devices.groupByBranch";

// Remembered per browser; storage can be unavailable (private window), which just means "off".
const readGroupPref = () => {
  try { return localStorage.getItem(GROUP_BY_BRANCH_KEY) === "1"; } catch { return false; }
};
const writeGroupPref = (on) => {
  try { localStorage.setItem(GROUP_BY_BRANCH_KEY, on ? "1" : "0"); } catch { /* ignore */ }
};

const Devices_list = () => {
  const userType = localStorage.getItem("userType");
  const merchantId = localStorage.getItem("MerchantId");
  const { selectedMerchant: reduxSelectedMerchant } = useSelector((state) => state.merchant || {});
  const selectedMerchant = userType === "merchant" ? merchantId : reduxSelectedMerchant;

  const [modal, setModal] = useState(false);
  // Only the id is held: the modal reads the live row from allDevices, so a save anywhere
  // (this device, or a sibling that lost a channel) shows up in both the table and the modal.
  const [selectedDeviceId, setSelectedDeviceId] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [branches, setBranches] = useState([]);
  const [displayedBranches, setDisplayedBranches] = useState([]);
  const [filteredDevices, setFilteredDevices] = useState([]);
  const [selectedBranchOptions, setSelectedBranchOptions] = useState([]); // [{value,label}]
  const [actionModal, setActionModal] = useState(false);
  const [deviceForAction, setDeviceForAction] = useState(null);
  const [allDevices, setAllDevices] = useState([]);
  const [floorsByBranch, setFloorsByBranch] = useState({}); // branchId -> [{id,name}]
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [lastUpdatedHours, setLastUpdatedHours] = useState(0);
  const [selectedAction, setSelectedAction] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [sortColumn, setSortColumn] = useState(null);
  const [sortDirection, setSortDirection] = useState('desc');
  const [reduxState, setReduxState] = useState(null);
  const [isFetchingReduxState, setIsFetchingReduxState] = useState(false);
  const [showOrdersModal, setShowOrdersModal] = useState(false);
  const [orderStats, setOrderStats] = useState(null);
  const [isFetchingOrderStats, setIsFetchingOrderStats] = useState(false);
  const [showOrderStatsModal, setShowOrderStatsModal] = useState(false);
  const [errorLogs, setErrorLogs] = useState(null);
  const [isFetchingErrorLogs, setIsFetchingErrorLogs] = useState(false);
  const [showErrorLogsModal, setShowErrorLogsModal] = useState(false);
  const [errorLogsPage, setErrorLogsPage] = useState(1);
  const [errorLogsLimit, setErrorLogsLimit] = useState(20);
  const [errorLogsCategory, setErrorLogsCategory] = useState(null);
  const [selectedDeviceForErrors, setSelectedDeviceForErrors] = useState(null);
  const [selectedErrorForDetails, setSelectedErrorForDetails] = useState(null);
  const [showErrorDetailsModal, setShowErrorDetailsModal] = useState(false);
  const [networkDiagnostics, setNetworkDiagnostics] = useState(null);
  const [isFetchingDiagnostics, setIsFetchingDiagnostics] = useState(false);
  const [showDiagnosticsModal, setShowDiagnosticsModal] = useState(false);
  const [orderStatsFromDate, setOrderStatsFromDate] = useState('');
  const [orderStatsToDate, setOrderStatsToDate] = useState('');
  const [orderStatsDevice, setOrderStatsDevice] = useState(null);
  // Channel routing
  const [channelOptions, setChannelOptions] = useState({ catalog: [] });
  // The merchant's Deliverect virtual locations, for the per-device brand picker.
  const [virtualLocations, setVirtualLocations] = useState([]);
  const [routingFilter, setRoutingFilter] = useState("all");
  const [groupByBranch, setGroupByBranch] = useState(readGroupPref);
  const [collapsedBranches, setCollapsedBranches] = useState({}); // branchKey -> true
  // Collapsed by default: the header line already says how many branches route by channel.
  const [showRoutingSummary, setShowRoutingSummary] = useState(false);

  const selectedDevice = useMemo(
    () => allDevices.find((d) => d.id === selectedDeviceId) || null,
    [allDevices, selectedDeviceId]
  );

  const toggleModal = () => setModal(!modal);

  const handleViewClick = (device) => {
    setSelectedDeviceId(device.id);
    setModal(true);
  };

  const handleToggleGroup = () => {
    setGroupByBranch((prev) => {
      writeGroupPref(!prev);
      return !prev;
    });
  };
  const toggleActionModal = () => setActionModal(!actionModal);
  const handleActionClick = (device) => { setDeviceForAction(device); setSelectedAction(null); toggleActionModal(); };

  const handleActionSubmit = async () => {
    if (!selectedAction || !deviceForAction?.deviceActionId) return;
    setIsSubmitting(true);
    const actionMap = { restart: 'restart-app', reset: 'refresh-app' };
    try {
      const success = await deviceAction({ id: deviceForAction?.deviceActionId, action: actionMap[selectedAction] });
      if (success) toggleActionModal();
    } catch (error) {} finally { setIsSubmitting(false); }
  };

  const fetchBranchesData = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await fetchBranches();
      setBranches(response.data);
      setDisplayedBranches(response.data);
    } catch (error) { console.error("Error fetching branches:", error); }
    finally { setIsLoading(false); }
  }, []);

  useEffect(() => { fetchBranchesData(); }, [fetchBranchesData]);

  const fetchDataAndSetRows = async () => {
    setIsLoading(true);
    try {
      const params = {};
      if (selectedMerchant && selectedMerchant !== "all") params.merchantId = selectedMerchant;
      // Branch filtering is applied client-side (multi-select), so fetch all merchant devices.
      const response = await fetchDevices(params);
      setAllDevices(response?.data || []);
    } catch (error) { console.error("Error fetching devices:", error); }
    finally { setIsLoading(false); }
  };

  useEffect(() => { fetchDataAndSetRows(); }, [selectedMerchant]);

  // Reset the branch filter when the merchant changes (its branches no longer apply).
  useEffect(() => { setSelectedBranchOptions([]); }, [selectedMerchant]);

  // Floors for the selected merchant, grouped by branch — used for the per-device
  // floor selector that drives waiter-order routing.
  useEffect(() => {
    const loadFloors = async () => {
      if (!selectedMerchant || selectedMerchant === "all") { setFloorsByBranch({}); return; }
      const floors = await getFloors({ MerchantId: selectedMerchant });
      const map = {};
      (floors || []).forEach((f) => {
        const bId = f.branchId;
        if (!map[bId]) map[bId] = [];
        map[bId].push({ id: f.id, name: f.name });
      });
      setFloorsByBranch(map);
    };
    loadFloors();
  }, [selectedMerchant]);

  // Options for the per-device channel picker. A fixed list, the same for every merchant, so it
  // is loaded once.
  useEffect(() => {
    let cancelled = false;
    fetchRoutableChannels().then((data) => {
      if (!cancelled) setChannelOptions(data || { catalog: [] });
    });
    return () => { cancelled = true; };
  }, []);

  // Virtual locations are per merchant, so they load only once a merchant is selected.
  useEffect(() => {
    let cancelled = false;
    if (!selectedMerchant || selectedMerchant === "all") { setVirtualLocations([]); return undefined; }
    getDeliverectVirtualLocations(selectedMerchant, { quiet: true }).then((rows) => {
      if (!cancelled) setVirtualLocations(rows || []);
    });
    return () => { cancelled = true; };
  }, [selectedMerchant]);

  // Branch routing, computed from every device of the branch (not the status-filtered list):
  // hiding offline tablets must not change what the page says about who receives which order.
  const branchSummaries = useMemo(() => summarizeBranches(allDevices), [allDevices]);
  const summaryByBranch = useMemo(() => {
    const map = new Map();
    branchSummaries.forEach((s) => map.set(s.branchId ?? "none", s));
    return map;
  }, [branchSummaries]);

  useEffect(() => {
    let devices = [...allDevices];
    if (selectedBranchOptions.length > 0) {
      const branchIds = selectedBranchOptions.map((o) => o.value);
      devices = devices.filter(d => branchIds.includes(d.BranchId ?? d.Branch?.id));
    }
    if (routingFilter !== "all") {
      const branchMatches = (d) => {
        const s = summaryByBranch.get(branchIdOf(d) ?? "none");
        if (!s) return false;
        if (routingFilter === "channel") return s.mode === "channel";
        if (routingFilter === "noChannel") return s.mode !== "channel";
        if (routingFilter === "issues") return s.issues.length > 0;
        return true;
      };
      devices = routingFilter === "claims"
        ? devices.filter((d) => routingClaimsOf(d).length > 0)
        : devices.filter(branchMatches);
    }
    if (selectedStatus === 'online') devices = devices.filter(d => d.isConnected === true);
    else if (selectedStatus === 'offline') devices = devices.filter(d => d.isConnected === false);
    else if (selectedStatus === 'stale') devices = devices.filter(d => d.staleOrdersAlert?.alertable);
    else if (selectedStatus === 'muted') devices = devices.filter(d => d.isActive === false || d.staleOrdersAlert?.suppressed);
    if (lastUpdatedHours > 0) {
      const now = new Date();
      const hoursInMs = lastUpdatedHours * 60 * 60 * 1000;
      // lastHeartbeatAt, not updatedAt: an admin muting a tablet must not make it look as
      // though the tablet checked in.
      devices = devices.filter(d => (now - new Date(d.lastHeartbeatAt || d.updatedAt)) >= hoursInMs);
    }
    if (sortColumn) {
      devices.sort((a, b) => {
        // Plain `a[col]` on a nested value sorts every row as "[object Object]", so the
        // computed columns need an accessor.
        const read = SORT_ACCESSORS[sortColumn] || ((d) => d[sortColumn]);
        let valA = read(a) || 0, valB = read(b) || 0;
        if (valA < valB) return sortDirection === 'asc' ? -1 : 1;
        if (valA > valB) return sortDirection === 'asc' ? 1 : -1;
        return 0;
      });
    } else {
      // Default order puts anything alerting at the top: the reason to open this page at all
      // is usually the row that needs attention, not the row with the highest id.
      devices.sort((a, b) => {
        const alertDiff = (b.staleOrdersAlert?.alertable ? 1 : 0) - (a.staleOrdersAlert?.alertable ? 1 : 0);
        if (alertDiff !== 0) return alertDiff;
        return sortDirection === 'asc' ? a.id - b.id : b.id - a.id;
      });
    }
    setFilteredDevices(devices);
  }, [selectedStatus, allDevices, lastUpdatedHours, sortColumn, sortDirection, selectedBranchOptions, routingFilter, summaryByBranch]);

  useEffect(() => {
    if (selectedMerchant && selectedMerchant !== "all") {
      setDisplayedBranches(branches?.filter(branch => branch?.MerchantId == selectedMerchant && branch?.active));
    } else { setDisplayedBranches(branches); }
  }, [selectedMerchant, branches]);

  const branchFilterOptions = (displayedBranches || []).map((b) => ({ value: b.id, label: b.name }));
  const handleSort = (column) => {
    if (sortColumn === column) setSortDirection(sortDirection === 'desc' ? 'asc' : 'desc');
    else { setSortColumn(column); setSortDirection('desc'); }
  };

  const toggleOrdersModal = () => setShowOrdersModal(!showOrdersModal);
  const handleFetchReduxState = async () => {
    if (!selectedDevice?.deviceActionId) return;
    setIsFetchingReduxState(true); setReduxState(null);
    try {
      const response = await fetchDeviceReduxState(selectedDevice.deviceActionId);
      if (response?.data?.result?.reduxState) { setReduxState(response.data.result.reduxState); toggleOrdersModal(); }
    } catch (error) { console.error("Error fetching redux state:", error); }
    finally { setIsFetchingReduxState(false); }
  };

  const handleFetchOrderStats = async (device, fromDate, toDate) => {
    if (!device?.deviceActionId) return;
    setIsFetchingOrderStats(true); setOrderStats(null); setOrderStatsDevice(device); setShowOrderStatsModal(true);
    try {
      const response = await fetchOrderStatsByDate(device.deviceActionId, { fromDate, toDate });
      if (response?.data?.result) setOrderStats(response.data.result);
      else if (response) setOrderStats(response);
    } catch (error) { console.error("Error fetching order statistics:", error); }
    finally { setIsFetchingOrderStats(false); }
  };

  const toggleOrderStatsModal = () => {
    setShowOrderStatsModal(!showOrderStatsModal);
    if (showOrderStatsModal) { setOrderStatsFromDate(''); setOrderStatsToDate(''); setOrderStatsDevice(null); }
  };
  const handleApplyDateFilter = () => { if (orderStatsDevice) handleFetchOrderStats(orderStatsDevice, orderStatsFromDate || null, orderStatsToDate || null); };
  const handleResetDateFilter = () => { setOrderStatsFromDate(''); setOrderStatsToDate(''); if (orderStatsDevice) handleFetchOrderStats(orderStatsDevice); };

  const toggleErrorDetailsModal = () => setShowErrorDetailsModal(!showErrorDetailsModal);
  const handleFetchDiagnostics = async (device) => {
    if (!device?.deviceActionId) return;
    setIsFetchingDiagnostics(true); setNetworkDiagnostics(null); setShowDiagnosticsModal(true);
    try {
      const response = await fetchNetworkDiagnostics(device.deviceActionId);
      if (response?.data?.result) setNetworkDiagnostics(response.data.result);
      else if (response) setNetworkDiagnostics(response);
    } catch (error) { console.error("Error fetching network diagnostics:", error); }
    finally { setIsFetchingDiagnostics(false); }
  };
  const toggleDiagnosticsModal = () => setShowDiagnosticsModal(!showDiagnosticsModal);
  const handleViewErrorDetails = (error) => { setSelectedErrorForDetails(error); setShowErrorDetailsModal(true); };

  const handleFetchErrorLogs = async (device, options = {}) => {
    if (!device?.deviceActionId) return;
    const { resetFilters = true, page = errorLogsPage, limit = errorLogsLimit, category = errorLogsCategory } = options;
    setSelectedDeviceForErrors(device); setIsFetchingErrorLogs(true); setShowErrorLogsModal(true);
    if (resetFilters) { setErrorLogsPage(1); setErrorLogsLimit(20); setErrorLogsCategory(null); setErrorLogs(null); }
    else { setErrorLogsPage(page); setErrorLogsLimit(limit); setErrorLogsCategory(category); }
    try {
      const response = await fetchErrorLogs(device.deviceActionId, { limit: resetFilters ? 20 : limit, page: resetFilters ? 1 : page, category: resetFilters ? null : category });
      if (response?.data?.result) setErrorLogs(response.data.result);
      else if (response) setErrorLogs(response);
    } catch (error) { console.error("Error fetching error logs:", error); }
    finally { setIsFetchingErrorLogs(false); }
  };

  const handleErrorLogsPageChange = (newPage) => { if (selectedDeviceForErrors) handleFetchErrorLogs(selectedDeviceForErrors, { resetFilters: false, page: newPage, limit: errorLogsLimit, category: errorLogsCategory }); };
  const handleErrorLogsLimitChange = (newLimit) => { if (selectedDeviceForErrors) handleFetchErrorLogs(selectedDeviceForErrors, { resetFilters: false, page: 1, limit: newLimit, category: errorLogsCategory }); };
  const handleErrorLogsCategoryChange = (category) => { if (selectedDeviceForErrors) handleFetchErrorLogs(selectedDeviceForErrors, { resetFilters: false, page: 1, limit: errorLogsLimit, category: category }); };
  const toggleErrorLogsModal = () => { setShowErrorLogsModal(!showErrorLogsModal); if (showErrorLogsModal) setSelectedDeviceForErrors(null); };

  // ─── Computed ────────────────────────────────────────────
  const activeOrders = filteredDevices?.reduce((sum, d) => sum + (d.activeOrdersCount || 0), 0) || 0;
  const unsyncedOrders = filteredDevices?.reduce((sum, d) => sum + (d.unsyncOrdersCount || 0), 0) || 0;
  const offlineLoginCount = filteredDevices?.filter(d => d?.isOfflineLogin).length || 0;
  const staleCount = filteredDevices?.filter(d => d.staleOrdersAlert?.alertable).length || 0;
  const channelBranchCount = branchSummaries.filter((s) => s.mode === "channel").length;
  const routingIssueCount = branchSummaries.filter((s) => s.issues.length > 0).length;
  // The summary card describes the branches the admin is looking at: all of the merchant's,
  // or only those picked in the branch filter.
  const visibleSummaries = selectedBranchOptions.length > 0
    ? branchSummaries.filter((s) => selectedBranchOptions.some((o) => o.value === s.branchId))
    : branchSummaries;
  const showBranchFeatures = (selectedMerchant && selectedMerchant !== "all") || userType === "merchant";

  // Group rows under their branch, keeping the table's current sort inside each group.
  const deviceGroups = useMemo(() => {
    const groups = new Map();
    filteredDevices.forEach((d) => {
      const key = branchIdOf(d) ?? "none";
      if (!groups.has(key)) groups.set(key, { key, name: d?.Branch?.name || "No branch", devices: [] });
      groups.get(key).devices.push(d);
    });
    return [...groups.values()].sort((a, b) => String(a.name).localeCompare(String(b.name)));
  }, [filteredDevices]);

  const toggleBranchCollapsed = (key) => setCollapsedBranches((prev) => ({ ...prev, [key]: !prev[key] }));
  const allCollapsed = deviceGroups.length > 0 && deviceGroups.every((g) => collapsedBranches[g.key]);
  const setAllCollapsed = (collapsed) =>
    setCollapsedBranches(collapsed ? Object.fromEntries(deviceGroups.map((g) => [g.key, true])) : {});

  // Narrow the table to one branch from the summary card.
  const focusBranch = (summary) => {
    if (summary.branchId == null) return;
    setSelectedBranchOptions([{ value: summary.branchId, label: summary.branchName }]);
  };

  // ─── Action icons ────────────────────────────────────────
  const ActionIcon = ({ icon: Icon, color, title, onClick }) => (
    <div onClick={onClick} title={title}
      style={{ width: 32, height: 32, borderRadius: 8, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", transition: "all 0.15s", border: `1px solid ${C.border}`, background: C.white }}
      onMouseEnter={e => { e.currentTarget.style.background = C.coralLight; e.currentTarget.style.borderColor = C.coral; }}
      onMouseLeave={e => { e.currentTarget.style.background = C.white; e.currentTarget.style.borderColor = C.border; }}>
      <Icon size={15} color={color} />
    </div>
  );

  // ─── Stale-orders badge ──────────────────────────────────
  // Orange while it needs action, grey once someone has taken responsibility for it. A
  // suppressed device keeps its badge rather than losing it: whoever muted it should still
  // see that the backlog is there.
  const StalePill = ({ alert }) => {
    if (!alert?.isStale) return null;

    const parts = [];
    if (alert.unsynced.isStale) {
      parts.push(`Unsynced since ${formatCustomDateTime(alert.unsynced.since)}${alert.unsynced.exact ? "" : " (approx.)"}`);
    }
    if (alert.active.isStale) {
      parts.push(`Open orders since ${formatCustomDateTime(alert.active.since)}`);
    }
    if (alert.suppressed === "muted") {
      parts.push(`Muted until ${formatCustomDateTime(alert.mutedUntil)}`);
    }
    if (alert.suppressed === "inactive") parts.push("Marked inactive");

    const label = alert.suppressed === "muted"
      ? `Stale · muted`
      : alert.suppressed === "inactive"
        ? `Stale · inactive`
        : `⚠ Stale · ${alert.maxAgeDays}d`;

    return (
      <span
        title={parts.join(" — ")}
        style={{
          padding: "4px 8px", borderRadius: 20, fontSize: 10, fontWeight: 700,
          background: alert.suppressed ? C.bg : C.orangeLight,
          color: alert.suppressed ? C.textLight : C.orange,
          border: alert.suppressed ? `1px solid ${C.borderLight}` : "none",
        }}
      >
        {label}
      </span>
    );
  };

  // ─── Info badge (for device name row) ────────────────────
  const InfoBadge = ({ label, value }) => (
    <span style={{ display: "inline-block", padding: "3px 8px", borderRadius: 6, fontSize: 10, fontWeight: 600, background: C.bg, color: C.textMed, marginRight: 4, marginBottom: 4, border: `1px solid ${C.borderLight}` }}>{label}: {value}</span>
  );

  // ─── Sort header ─────────────────────────────────────────
  const SortTh = ({ col, children }) => (
    <th style={{ ...tbl.th, cursor: "pointer", userSelect: "none" }} onClick={() => handleSort(col)}>
      {children} {sortColumn === col && <span style={{ color: C.coral }}>{sortDirection === 'asc' ? '▲' : '▼'}</span>}
    </th>
  );

  // ─── Filter select style ─────────────────────────────────
  const filterSelectStyle = {
    padding: "7px 12px", fontSize: 12, borderRadius: 8, border: `1.5px solid ${C.border}`, fontFamily: "inherit",
    outline: "none", color: C.text, background: C.white, cursor: "pointer", minWidth: 130, transition: "border 0.15s",
  };

  // react-select styles for the branch multi-select, matching the plain filter dropdowns above.
  const branchSelectStyles = {
    control: (base, state) => ({
      ...base,
      minHeight: 34,
      borderRadius: 8,
      border: `1.5px solid ${state.isFocused ? C.coral : C.border}`,
      boxShadow: "none",
      fontSize: 12,
      backgroundColor: C.white,
      cursor: "pointer",
      "&:hover": { borderColor: state.isFocused ? C.coral : C.border },
    }),
    valueContainer: (base) => ({ ...base, padding: "1px 8px" }),
    placeholder: (base) => ({ ...base, color: C.textLight, fontSize: 12 }),
    input: (base) => ({ ...base, margin: 0, padding: 0, fontSize: 12, color: C.text }),
    indicatorsContainer: (base) => ({ ...base, height: 32 }),
    indicatorSeparator: (base) => ({ ...base, backgroundColor: C.border }),
    menu: (base) => ({ ...base, fontSize: 12, zIndex: 30 }),
    multiValue: (base) => ({ ...base, backgroundColor: C.bg }),
    multiValueLabel: (base) => ({ ...base, color: C.text, fontSize: 12 }),
  };

  // ─── Branch routing card (summary grid) ──────────────────
  const BranchRoutingCard = ({ summary }) => {
    const hasIssues = summary.issues.length > 0;
    return (
      <div
        onClick={() => focusBranch(summary)}
        title="Show only this branch's devices"
        style={{
          border: `1px solid ${hasIssues ? "#fde0b0" : C.border}`, borderLeft: `4px solid ${hasIssues ? C.orange : summary.mode === "channel" ? C.coral : C.border}`,
          borderRadius: 10, padding: 12, background: C.white, cursor: "pointer", display: "flex", flexDirection: "column", gap: 8,
        }}
      >
        <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 8 }}>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontWeight: 700, fontSize: 13, color: C.text, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{summary.branchName}</div>
            <div style={{ fontSize: 11, color: C.textLight, fontWeight: 600 }}>
              {summary.total} device{summary.total !== 1 ? "s" : ""} · {summary.online} online · {summary.receiving.length} taking online orders
            </div>
          </div>
          <RoutingModeBadge mode={summary.mode} short />
        </div>

        {summary.claims.length > 0 && (
          <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            {summary.claims.map(({ device: d, channels, effective }) => (
              <div key={d.id} style={{ display: "flex", alignItems: "baseline", gap: 6, fontSize: 12 }}>
                <span style={{ fontWeight: 600, color: effective ? C.text : C.textLight, flexShrink: 0 }}>{deviceLabel(d)}</span>
                <span style={{ color: C.textLight }}>→</span>
                <span style={{ color: effective ? C.coralDark : C.textLight, textDecoration: effective ? "none" : "line-through", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={formatChannelNames(channels, 99)}>
                  {formatChannelNames(channels, 4)}
                </span>
              </div>
            ))}
          </div>
        )}

        <div style={{ fontSize: 12, color: C.textMed }}>
          {summary.mode === "channel" ? "Unclaimed channels" : "Online orders"} → <strong>{summary.fallbackText}</strong>
        </div>

        {hasIssues && (
          <div style={{ fontSize: 11, color: "#9a5b00", background: C.orangeLight, borderRadius: 6, padding: "6px 8px", lineHeight: 1.45 }}>
            {summary.issues.map((issue) => <div key={issue}>⚠ {issue}</div>)}
          </div>
        )}
      </div>
    );
  };

  // ─── One device row (flat and grouped views) ─────────────
  const renderDeviceRow = (device, { inGroup = false } = {}) => {
    const sa = device.staleOrdersAlert;
    const channels = routingClaimsOf(device);
    const claimsCount = channels.length > 0 && isReceiving(device);
    return (
      <tr key={device.id} className="rpt-row"
        style={sa?.alertable
          ? { borderLeft: `4px solid ${C.orange}`, background: C.orangeLight }
          : sa?.isStale
            ? { borderLeft: `4px solid ${C.border}` }
            : undefined}>
        <td style={{ ...tbl.td, color: C.textLight, fontWeight: 600, ...(inGroup ? { paddingLeft: 28 } : {}) }}>{device?.id}</td>
        <td style={tbl.td}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
            <span style={{ fontWeight: 700, color: C.text, maxWidth: 220, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={device?.name}>{device?.name}</span>
            <StalePill alert={sa} />
          </div>
          {channels.length > 0 && (
            <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", marginBottom: 2 }} title={claimsCount ? "Aggregator orders on these virtual locations and channels come only to this tablet" : "These claims have no effect while online orders are off or the tablet is inactive"}>
              <span style={{ fontSize: 10, fontWeight: 700, color: C.textLight, textTransform: "uppercase", letterSpacing: 0.4, marginRight: 6, marginBottom: 4 }}>Routes</span>
              {channels.slice(0, 4).map((c) => <ChannelChip key={c.id} name={c.name} muted={!claimsCount} />)}
              {channels.length > 4 && <span style={{ fontSize: 11, color: C.textMed, marginBottom: 4 }}>+{channels.length - 4}</span>}
            </div>
          )}
          <div style={{ display: "flex", flexWrap: "wrap" }}>
            {!inGroup && device?.Branch?.name && <InfoBadge label="Branch" value={device.Branch.name} />}
            {device?.Floor?.name && <InfoBadge label="Floor" value={device.Floor.name} />}
            {device?.isPrimaryDevice && <InfoBadge label="Primary" value="Yes" />}
            {device?.isActive === false && <InfoBadge label="Inactive" value="Yes" />}
            {device?.staleAlertsMutedUntil && new Date(device.staleAlertsMutedUntil) > new Date() && <InfoBadge label="Muted" value={`until ${formatCustomDateTime(device.staleAlertsMutedUntil)}`} />}
            <InfoBadge label="Online Orders" value={device?.onlineOrdersEnabled ? "Enabled" : "Off"} />
            {device?.cashboxName && <InfoBadge label="Cashbox" value={device.cashboxName} />}
            {/* Heartbeat, not updatedAt — an admin action on this row must not read as contact from the tablet. */}
            <InfoBadge label="Updated" value={getTimeAgo(device.lastHeartbeatAt || device.updatedAt)} />
            {device?.macAddress && <InfoBadge label="MAC" value={device.macAddress} />}
          </div>
        </td>
        <td style={{ ...tbl.td, textAlign: "center", fontWeight: 700 }}>
          {device?.activeOrdersCount}
          {/* Two very different things live inside that number. */}
          {(sa?.cancelled?.count > 0 || sa?.paidCount > 0) && (
            <div style={{ fontSize: 10, fontWeight: 600, color: C.textLight, marginTop: 2 }}>
              {sa.cancelled.count > 0 && <span title="Cancelled orders never cleared off the till. These never trigger an alert.">{sa.cancelled.count} cancelled</span>}
              {sa.cancelled.count > 0 && sa.paidCount > 0 && " · "}
              {sa.paidCount > 0 && <span title="Open orders that already carry a payment.">{sa.paidCount} paid</span>}
            </div>
          )}
        </td>
        <td style={{ ...tbl.td, textAlign: "center", fontWeight: 700, color: device?.unsyncOrdersCount > 0 ? C.orange : C.textMed }}>
          {device?.unsyncOrdersCount}
          {sa?.unsynced?.since && (
            <div style={{ fontSize: 10, fontWeight: 600, color: C.textLight, marginTop: 2 }}
              title={sa.unsynced.exact ? "Reported by the tablet" : "Approximate: from when the server first saw this backlog"}>
              {getTimeAgo(sa.unsynced.since)}{sa.unsynced.exact ? "" : "*"}
            </div>
          )}
        </td>
        <td style={{ ...tbl.td, textAlign: "center", fontWeight: 700, color: sa?.alertable ? C.orange : C.textLight }}>
          {sa?.maxAgeDays ? `${sa.maxAgeDays}d` : "—"}
        </td>
        <td style={tbl.td}>
          <StatusPill connected={device.isConnected} offlineLogin={device?.isOfflineLogin} />
          {device?.keyPendingRequest && <div style={{ marginTop: 4 }}><KeyStatusPill pending pendingOnly /></div>}
        </td>
        <td style={tbl.td}>
          <div style={{ display: "flex", justifyContent: "center", gap: 6 }}>
            <ActionIcon icon={Eye} color={C.coral} title="View Details" onClick={() => handleViewClick(device)} />
            {device.isConnected && <>
              <ActionIcon icon={ShoppingCart} color={C.blue} title="Order Stats" onClick={() => handleFetchOrderStats(device)} />
              <ActionIcon icon={AlertTriangle} color="#dc3545" title="Error Logs" onClick={() => handleFetchErrorLogs(device)} />
              <ActionIcon icon={Wifi} color="#17a2b8" title="Diagnostics" onClick={() => handleFetchDiagnostics(device)} />
              <ActionIcon icon={RefreshCw} color={C.green} title="Device Action" onClick={() => handleActionClick(device)} />
            </>}
          </div>
        </td>
      </tr>
    );
  };

  // ─── Branch header row (grouped view) ────────────────────
  const renderGroupHeader = (group) => {
    const s = summaryByBranch.get(group.key);
    const collapsed = !!collapsedBranches[group.key];
    const online = group.devices.filter((d) => d.isConnected).length;
    return (
      <tr key={`group-${group.key}`} onClick={() => toggleBranchCollapsed(group.key)} style={{ cursor: "pointer", background: C.bg }}>
        <td colSpan={7} style={{ ...tbl.td, padding: "10px 16px", borderBottom: `1px solid ${C.border}` }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
            {collapsed ? <ChevronRight size={16} color={C.textMed} /> : <ChevronDown size={16} color={C.textMed} />}
            <span style={{ fontWeight: 700, fontSize: 13, color: C.text }}>{group.name}</span>
            <span style={{ fontSize: 11, color: C.textLight, fontWeight: 600 }}>
              {group.devices.length} shown · {online} online
            </span>
            {s && <RoutingModeBadge mode={s.mode} />}
            {s?.mode === "channel" && (
              <span style={{ fontSize: 11, color: C.textMed }}>
                {s.claimedCount} channel{s.claimedCount !== 1 ? "s" : ""} / location{s.claimedCount !== 1 ? "s" : ""} claimed · rest → {s.fallbackText}
              </span>
            )}
            {s?.issues.length > 0 && (
              <span title={s.issues.join("\n")} style={{ fontSize: 10, fontWeight: 700, color: C.orange, background: C.orangeLight, padding: "3px 8px", borderRadius: 20 }}>
                ⚠ {s.issues.length} routing issue{s.issues.length !== 1 ? "s" : ""}
              </span>
            )}
          </div>
        </td>
      </tr>
    );
  };

  return (
    <Fragment>
      <Breadcrumb title="Devices" parent="Devices" />
      <Container fluid>
        <style>{reportingGlobalCSS}</style>
        <div className="rpt-wrap">

          {/* ─── KPI Tiles ─────────────────────────────── */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 16, marginBottom: 24 }}>
            <Tile label="Total Devices" value={filteredDevices?.length || 0} icon="📱" gradient={`linear-gradient(135deg, ${C.coral}, #00bfa5)`} delay={0} />
            <Tile label="Active Orders" value={activeOrders} icon="🛒" gradient={`linear-gradient(135deg, ${C.blue}, #A8B8FF)`} delay={180} />
            <Tile label="Unsynced" value={unsyncedOrders} icon="⚠️" gradient={`linear-gradient(135deg, ${C.orange}, #FFD200)`} delay={240} />
            <Tile label="Stale Orders" value={staleCount} icon="⏳" gradient={`linear-gradient(135deg, #E74C3C, ${C.orange})`} delay={300} />
            <Tile label="Offline Login" value={offlineLoginCount} icon="🔑" gradient={`linear-gradient(135deg, ${C.purple}, #C39BD3)`} delay={360} />
            {showBranchFeatures && (
              <Tile
                label="Channel Routing"
                value={`${channelBranchCount}/${branchSummaries.length}`}
                sub={routingIssueCount > 0 ? `${routingIssueCount} branch${routingIssueCount !== 1 ? "es" : ""} need attention` : "branches"}
                icon="🔀"
                gradient={`linear-gradient(135deg, ${C.coral}, ${C.blue})`}
                delay={420}
              />
            )}
          </div>

          {/* ─── Order routing by branch ───────────────── */}
          {showBranchFeatures && branchSummaries.length > 0 && (
            <RCard>
              <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", marginBottom: showRoutingSummary ? 14 : 0 }}>
                <div style={{ fontSize: 14, fontWeight: 700, color: C.text }}>Online Order Routing by Branch</div>
                <span style={{ fontSize: 12, color: C.textMed }}>
                  {channelBranchCount} of {branchSummaries.length} branch{branchSummaries.length !== 1 ? "es" : ""} use channel routing
                  {routingIssueCount > 0 && <span style={{ color: C.orange, fontWeight: 600 }}> · {routingIssueCount} with issues</span>}
                </span>
                <div style={{ marginLeft: "auto", display: "flex", gap: 8 }}>
                  {selectedBranchOptions.length > 0 && (
                    <Button size="sm" color="link" style={{ color: C.textMed, fontSize: 12, padding: 0 }} onClick={() => setSelectedBranchOptions([])}>
                      Show all branches
                    </Button>
                  )}
                  <Button size="sm" color="link" style={{ color: C.coral, fontSize: 12, fontWeight: 600, padding: 0 }} onClick={() => setShowRoutingSummary((v) => !v)}>
                    {showRoutingSummary ? "Hide" : "Show"}
                  </Button>
                </div>
              </div>
              {showRoutingSummary && (
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: 12 }}>
                  {visibleSummaries.map((s) => <BranchRoutingCard key={s.branchId ?? "none"} summary={s} />)}
                </div>
              )}
            </RCard>
          )}

          {/* ─── Filter Bar + Table ────────────────────── */}
          <RCard>
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 16, flexWrap: "wrap" }}>
              <div onClick={fetchDataAndSetRows} style={{ width: 36, height: 36, borderRadius: 10, border: `1.5px solid ${C.border}`, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", transition: "all 0.15s", background: C.white, flexShrink: 0 }}
                onMouseEnter={e => { e.currentTarget.style.borderColor = C.coral; e.currentTarget.style.background = C.coralLight; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = C.border; e.currentTarget.style.background = C.white; }}>
                <RefreshCw size={15} color={C.textMed} />
              </div>

              <select value={selectedStatus} onChange={(e) => setSelectedStatus(e.target.value)} style={filterSelectStyle}>
                <option value="all">All Statuses</option>
                <option value="online">Online</option>
                <option value="offline">Offline</option>
                <option value="stale">Stale orders</option>
                <option value="muted">Muted / inactive</option>
              </select>

              <select value={lastUpdatedHours} onChange={(e) => setLastUpdatedHours(parseInt(e.target.value))} style={filterSelectStyle}>
                <option value={0}>Last Updated At</option>
                <option value={1}>1 hour ago</option>
                <option value={3}>3 hours ago</option>
                <option value={6}>6 hours ago</option>
                <option value={12}>12 hours ago</option>
                <option value={24}>1 day ago</option>
                <option value={48}>2 days ago</option>
                <option value={168}>1 week ago</option>
                <option value={336}>2 weeks ago</option>
                <option value={720}>1 month ago</option>
                <option value={2160}>3 months ago</option>
              </select>

              {((selectedMerchant && selectedMerchant !== 'all') || userType === "merchant") && (
                <div style={{ minWidth: 200 }}>
                  <MultiSelectField
                    options={branchFilterOptions}
                    value={selectedBranchOptions}
                    onChange={(vals) => setSelectedBranchOptions(vals || [])}
                    placeholder="All Branches"
                    styles={branchSelectStyles}
                  />
                </div>
              )}

              {showBranchFeatures && (
                <select value={routingFilter} onChange={(e) => setRoutingFilter(e.target.value)} style={filterSelectStyle}>
                  <option value="all">All Routing</option>
                  <option value="channel">Branches with channel / location routing</option>
                  <option value="noChannel">Branches without channel / location routing</option>
                  <option value="issues">Branches with routing issues</option>
                  <option value="claims">Devices that claim channels or locations</option>
                </select>
              )}

              <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 8 }}>
                {groupByBranch && deviceGroups.length > 1 && (
                  <Button size="sm" color="link" style={{ color: C.textMed, fontSize: 12, padding: 0 }} onClick={() => setAllCollapsed(!allCollapsed)}>
                    {allCollapsed ? "Expand all" : "Collapse all"}
                  </Button>
                )}
                <div style={{ display: "inline-flex", border: `1.5px solid ${C.border}`, borderRadius: 8, overflow: "hidden" }}>
                  {[{ on: false, icon: ListIcon, label: "List" }, { on: true, icon: Layers, label: "By branch" }].map(({ on, icon: Icon, label }) => {
                    const active = groupByBranch === on;
                    return (
                      <button
                        key={label}
                        type="button"
                        onClick={() => { if (!active) handleToggleGroup(); }}
                        style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "6px 12px", fontSize: 12, fontWeight: 600, border: "none", cursor: "pointer", background: active ? C.coralLight : C.white, color: active ? C.coral : C.textMed }}
                      >
                        <Icon size={13} /> {label}
                      </button>
                    );
                  })}
                </div>
                <span style={{ fontSize: 12, color: C.textLight, background: C.bg, padding: "4px 12px", borderRadius: 20 }}>{filteredDevices?.length || 0} devices</span>
              </div>
            </div>

            {isLoading ? <RLoader /> : (
              <div style={{ overflowX: "auto", borderRadius: 10, border: `1px solid ${C.border}` }}>
                <table style={tbl.table}>
                  <thead>
                    <tr>
                      <th style={tbl.th}>ID</th>
                      <th style={tbl.th}>Device</th>
                      <SortTh col="activeOrdersCount">Active Orders</SortTh>
                      <SortTh col="unsyncOrdersCount">Unsynced</SortTh>
                      <SortTh col="staleAgeDays">Oldest</SortTh>
                      <SortTh col="isConnected">Status</SortTh>
                      <th style={{ ...tbl.th, textAlign: "center" }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredDevices?.length === 0 ? (
                      <tr><td colSpan="7" style={{ ...tbl.td, textAlign: "center", padding: 40, color: C.textLight }}>No devices found</td></tr>
                    ) : groupByBranch ? (
                      deviceGroups.map((group) => (
                        <Fragment key={group.key}>
                          {renderGroupHeader(group)}
                          {!collapsedBranches[group.key] && group.devices.map((d) => renderDeviceRow(d, { inGroup: true }))}
                        </Fragment>
                      ))
                    ) : (
                      filteredDevices.map((d) => renderDeviceRow(d))
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </RCard>
        </div>
      </Container>

      {/* ─── Device Detail Modal ───────────────────────── */}
      <DeviceDetailsModal
        isOpen={modal && !!selectedDevice}
        toggle={toggleModal}
        device={selectedDevice}
        branchDevices={selectedDevice ? allDevices.filter((d) => branchIdOf(d) === branchIdOf(selectedDevice)) : []}
        floors={selectedDevice ? floorsByBranch[branchIdOf(selectedDevice)] || [] : []}
        channelOptions={channelOptions}
        virtualLocationOptions={selectedDevice ? virtualLocations.filter((v) => v.BranchId === branchIdOf(selectedDevice)) : []}
        merchantSelected={!!selectedMerchant && selectedMerchant !== "all"}
        onDevicesChange={setAllDevices}
        onViewKds={handleFetchReduxState}
        isFetchingKds={isFetchingReduxState}
      />

      {/* ─── Action Modal ──────────────────────────────── */}
      <Modal isOpen={actionModal} toggle={toggleActionModal} centered>
        <ModalHeader toggle={toggleActionModal} style={{ borderBottom: `2px solid ${C.borderLight}` }}>
          <span style={{ fontWeight: 700 }}>Device Action: {deviceForAction?.name}</span>
        </ModalHeader>
        <ModalBody style={{ padding: 32, fontFamily: "'Inter', sans-serif" }}>
          <p style={{ textAlign: "center", color: C.textMed, marginBottom: 24, fontSize: 13 }}>Select an action to perform on the device.</p>
          <div style={{ display: "flex", justifyContent: "center", gap: 20 }}>
            {[{ key: "reset", icon: "undo", label: "Reset Device" }, { key: "restart", icon: "power-off", label: "Restart Device" }].map(a => (
              <div key={a.key} onClick={() => setSelectedAction(a.key)}
                style={{
                  display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
                  padding: 24, borderRadius: 14, cursor: "pointer", width: 150, height: 120, transition: "all 0.15s",
                  border: selectedAction === a.key ? `2px solid ${C.coral}` : `2px solid ${C.border}`,
                  background: selectedAction === a.key ? C.coralLight : C.white,
                  boxShadow: selectedAction === a.key ? `0 0 16px rgba(0, 150, 137,0.15)` : "none",
                }}>
                <i className={`fa fa-${a.icon}`} style={{ fontSize: 32, color: C.coral, marginBottom: 10 }} />
                <span style={{ fontWeight: 700, fontSize: 13, color: C.text }}>{a.label}</span>
              </div>
            ))}
          </div>
        </ModalBody>
        <ModalFooter style={{ borderTop: `1px solid ${C.borderLight}` }}>
          {selectedAction && (
            <Button style={{ background: C.coral, border: "none", borderRadius: 10, fontWeight: 600, fontSize: 13 }} onClick={handleActionSubmit} disabled={isSubmitting}>
              {isSubmitting ? 'Submitting...' : 'Submit'}
            </Button>
          )}
        </ModalFooter>
      </Modal>

      {/* ─── KDS & Printer Modal ───────────────────────── */}
      <Modal isOpen={showOrdersModal} toggle={toggleOrdersModal} size="lg" centered>
        <ModalHeader toggle={toggleOrdersModal}><span style={{ fontWeight: 700 }}>KDS & Printers — {selectedDevice?.name}</span></ModalHeader>
        <ModalBody style={{ maxHeight: '70vh', overflowY: 'auto', fontFamily: "'Inter', sans-serif" }}>
          {reduxState ? (
            <div>
              {reduxState.printer?.printers && (
                <>
                  <RCard title="Main Printers">
                    {reduxState.printer.printers.filter(p => p.main).length > 0 ? reduxState.printer.printers.filter(p => p.main).map((printer, i) => (
                      <div key={i} style={{ padding: 16, background: C.bg, borderRadius: 10, border: `1px solid ${C.border}`, marginBottom: 10 }}>
                        <div style={{ display: "flex", flexWrap: "wrap", gap: 16, fontSize: 13 }}>
                          <span><strong>Name:</strong> {printer.name || 'N/A'}</span>
                          <span><strong>IP:</strong> {printer.ip || 'N/A'}</span>
                          {printer.bt && <span><strong>BT:</strong> {printer.bt}</span>}
                          {printer.target && <span><strong>Target:</strong> {printer.target}</span>}
                          {printer.printQty && <span><strong>Qty:</strong> {printer.printQty}</span>}
                        </div>
                        {printer.categories?.length > 0 && <div style={{ marginTop: 8, display: "flex", flexWrap: "wrap", gap: 4 }}>{printer.categories.map((cat, idx) => <span key={idx} style={{ padding: "3px 8px", borderRadius: 6, fontSize: 10, fontWeight: 600, background: C.blueLight, color: C.blue }}>{cat.name || 'N/A'}</span>)}</div>}
                      </div>
                    )) : <div style={{ color: C.textLight, textAlign: "center", padding: 16 }}>No main printers found</div>}
                  </RCard>
                  <RCard title="KDS Printers">
                    {reduxState.printer.printers.filter(p => !p.main).length > 0 ? reduxState.printer.printers.filter(p => !p.main).map((printer, i) => (
                      <div key={i} style={{ padding: 16, background: C.bg, borderRadius: 10, border: `1px solid ${C.border}`, marginBottom: 10 }}>
                        <div style={{ display: "flex", flexWrap: "wrap", gap: 16, fontSize: 13 }}>
                          <span><strong>Name:</strong> {printer.name || 'N/A'}</span>
                          <span><strong>IP:</strong> {printer.ip || 'N/A'}</span>
                          {printer.bt && <span><strong>BT:</strong> {printer.bt}</span>}
                        </div>
                        {printer.categories?.length > 0 && <div style={{ marginTop: 8, display: "flex", flexWrap: "wrap", gap: 4 }}>{printer.categories.map((cat, idx) => <span key={idx} style={{ padding: "3px 8px", borderRadius: 6, fontSize: 10, fontWeight: 600, background: C.blueLight, color: C.blue }}>{cat.name || 'N/A'}</span>)}</div>}
                      </div>
                    )) : <div style={{ color: C.textLight, textAlign: "center", padding: 16 }}>No KDS printers found</div>}
                  </RCard>
                </>
              )}
              {reduxState.settings?.kitchen && (
                <RCard title="KDS Connections">
                  {reduxState.settings.kitchen.length > 0 ? reduxState.settings.kitchen.map((kds, i) => {
                    const conn = reduxState.kdsConnection?.kdsConnections?.find(c => c.ip === kds.ip);
                    const isConn = conn?.connected || false;
                    return (
                      <div key={i} style={{ padding: 16, background: C.bg, borderRadius: 10, border: `2px solid ${isConn ? C.green : C.coral}`, marginBottom: 10 }}>
                        <div style={{ display: "flex", flexWrap: "wrap", gap: 16, fontSize: 13, marginBottom: 8 }}>
                          <span><strong>Name:</strong> {kds.name || 'N/A'}</span>
                          <span><strong>IP:</strong> {kds.ip || 'N/A'}</span>
                          <span><strong>Status:</strong> <span style={{ color: isConn ? C.green : C.coral, fontWeight: 700 }}>{isConn ? 'Connected' : 'Disconnected'}</span></span>
                          <span><strong>Last Ping:</strong> {conn?.time ? getTimeAgo(conn.time) : 'Never'}</span>
                        </div>
                        <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
                          {kds.orderTypes?.map((type, idx) => <span key={idx} style={{ padding: "3px 8px", borderRadius: 6, fontSize: 10, fontWeight: 600, background: C.orangeLight, color: C.orange }}>Type {type}</span>)}
                          {kds.categories?.map((cat, idx) => <span key={idx} style={{ padding: "3px 8px", borderRadius: 6, fontSize: 10, fontWeight: 600, background: C.blueLight, color: C.blue }}>{cat.name || cat.arabic_name || 'N/A'}</span>)}
                          {kds.products?.map((productId, idx) => {
                            const product = reduxState.products?.products?.find(p => p.id === productId);
                            return <span key={idx} style={{ padding: "3px 8px", borderRadius: 6, fontSize: 10, fontWeight: 600, background: C.greenLight, color: C.green }}>{product?.title || product?.name || `ID: ${productId}`}</span>;
                          })}
                        </div>
                      </div>
                    );
                  }) : <div style={{ color: C.textLight, textAlign: "center", padding: 16 }}>No KDS configured</div>}
                </RCard>
              )}
            </div>
          ) : <RLoader />}
        </ModalBody>
        <ModalFooter><Button style={{ background: C.bg, border: `1px solid ${C.border}`, color: C.text, borderRadius: 10, fontWeight: 600, fontSize: 13 }} onClick={toggleOrdersModal}>Close</Button></ModalFooter>
      </Modal>

      {/* ─── Order Stats Modal ─────────────────────────── */}
      <Modal isOpen={showOrderStatsModal} toggle={toggleOrderStatsModal} size="lg" centered>
        <ModalHeader toggle={toggleOrderStatsModal}><span style={{ fontWeight: 700 }}>Order Statistics</span></ModalHeader>
        <ModalBody style={{ maxHeight: '70vh', overflowY: 'auto', fontFamily: "'Inter', sans-serif" }}>
          <div style={{ display: "flex", alignItems: "flex-end", gap: 10, marginBottom: 20, flexWrap: "wrap" }}>
            <div><label style={{ fontSize: 11, fontWeight: 600, color: C.textLight, display: "block", marginBottom: 4 }}>From</label><input type="date" value={orderStatsFromDate} onChange={e => setOrderStatsFromDate(e.target.value)} style={{ padding: "7px 10px", borderRadius: 8, border: `1.5px solid ${C.border}`, fontSize: 12, fontFamily: "inherit" }} /></div>
            <div><label style={{ fontSize: 11, fontWeight: 600, color: C.textLight, display: "block", marginBottom: 4 }}>To</label><input type="date" value={orderStatsToDate} onChange={e => setOrderStatsToDate(e.target.value)} style={{ padding: "7px 10px", borderRadius: 8, border: `1.5px solid ${C.border}`, fontSize: 12, fontFamily: "inherit" }} /></div>
            <button onClick={handleApplyDateFilter} disabled={isFetchingOrderStats} style={{ padding: "7px 16px", borderRadius: 8, background: C.coral, color: "white", border: "none", fontSize: 12, fontWeight: 600, cursor: "pointer" }}>Apply</button>
            <button onClick={handleResetDateFilter} disabled={isFetchingOrderStats} style={{ padding: "7px 16px", borderRadius: 8, background: C.bg, color: C.text, border: `1px solid ${C.border}`, fontSize: 12, fontWeight: 600, cursor: "pointer" }}>Reset</button>
          </div>
          {isFetchingOrderStats ? <RLoader /> : orderStats ? (
            <div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 20 }}>
                <Tile label="Running Orders" value={orderStats.totalRunningOrders || 0} icon="🛒" gradient={`linear-gradient(135deg, ${C.blue}, #A8B8FF)`} />
                <Tile label="Unsynced Orders" value={orderStats.totalUnsyncedOrders || 0} icon="⚠️" gradient={`linear-gradient(135deg, ${C.orange}, #FFD200)`} />
              </div>
              {orderStats.runningOrders?.length > 0 && <RCard title="Running Orders by Date"><div style={{ overflowX: "auto", borderRadius: 10, border: `1px solid ${C.border}` }}><table style={tbl.table}><thead><tr>{["Date", "Active", "Cancelled", "Total"].map(h => <th key={h} style={tbl.th}>{h}</th>)}</tr></thead><tbody>{orderStats.runningOrders.map((item, i) => <tr key={i} className="rpt-row"><td style={tbl.td}>{item.date}</td><td style={{ ...tbl.td, color: C.green, fontWeight: 700 }}>{item.activeCount}</td><td style={{ ...tbl.td, color: C.coral, fontWeight: 700 }}>{item.cancelledCount}</td><td style={{ ...tbl.td, fontWeight: 700 }}>{item.totalCount}</td></tr>)}</tbody></table></div></RCard>}
              {orderStats.unsyncedOrders?.length > 0 && <RCard title="Unsynced Orders by Date"><div style={{ overflowX: "auto", borderRadius: 10, border: `1px solid ${C.border}` }}><table style={tbl.table}><thead><tr>{["Date", "Count"].map(h => <th key={h} style={tbl.th}>{h}</th>)}</tr></thead><tbody>{orderStats.unsyncedOrders.map((item, i) => <tr key={i} className="rpt-row"><td style={tbl.td}>{item.date}</td><td style={{ ...tbl.td, fontWeight: 700, color: C.orange }}>{item.count}</td></tr>)}</tbody></table></div></RCard>}
            </div>
          ) : <div style={{ textAlign: "center", padding: 40, color: C.textLight }}>Failed to load statistics</div>}
        </ModalBody>
        <ModalFooter><Button style={{ background: C.bg, border: `1px solid ${C.border}`, color: C.text, borderRadius: 10, fontWeight: 600, fontSize: 13 }} onClick={toggleOrderStatsModal}>Close</Button></ModalFooter>
      </Modal>

      {/* ─── Error Logs Modal ──────────────────────────── */}
      <Modal isOpen={showErrorLogsModal} toggle={toggleErrorLogsModal} size="xl" centered>
        <ModalHeader toggle={toggleErrorLogsModal}><span style={{ fontWeight: 700 }}>Error Logs & Diagnostics</span></ModalHeader>
        <div style={{ padding: "12px 20px", borderBottom: `1px solid ${C.borderLight}`, background: C.bg, display: "flex", alignItems: "flex-end", gap: 12, flexWrap: "wrap", fontFamily: "'Inter', sans-serif" }}>
          <div><label style={{ fontSize: 11, fontWeight: 600, color: C.textLight, display: "block", marginBottom: 4 }}>Category</label>
            <select value={errorLogsCategory || ''} onChange={e => handleErrorLogsCategoryChange(e.target.value || null)} disabled={isFetchingErrorLogs} style={{ ...filterSelectStyle, minWidth: 140 }}>
              <option value="">All Categories</option><option value="sync">Sync</option><option value="kds">KDS</option><option value="printer">Printer</option><option value="payment">Payment</option><option value="network">Network</option>
            </select>
          </div>
          <div><label style={{ fontSize: 11, fontWeight: 600, color: C.textLight, display: "block", marginBottom: 4 }}>Per Page</label>
            <select value={errorLogsLimit} onChange={e => handleErrorLogsLimitChange(parseInt(e.target.value))} disabled={isFetchingErrorLogs} style={{ ...filterSelectStyle, minWidth: 80 }}>
              <option value="10">10</option><option value="20">20</option><option value="50">50</option><option value="100">100</option>
            </select>
          </div>
          {errorLogs && <span style={{ fontSize: 12, color: C.textMed }}>Showing {((errorLogsPage - 1) * errorLogsLimit) + 1}–{Math.min(errorLogsPage * errorLogsLimit, errorLogs.totalErrors)} of {errorLogs.totalErrors}</span>}
        </div>
        <ModalBody style={{ maxHeight: '60vh', overflowY: 'auto', fontFamily: "'Inter', sans-serif" }}>
          {isFetchingErrorLogs ? <RLoader /> : errorLogs ? (
            <div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 2fr", gap: 16, marginBottom: 20 }}>
                <Tile label="Total Errors" value={errorLogs.totalErrors || 0} icon="🔴" gradient={`linear-gradient(135deg, #E74C3C, #FF6B6B)`} />
                <RCard title="Error Breakdown" style={{ marginBottom: 0 }}>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                    {errorLogs.errorStats?.length > 0 ? errorLogs.errorStats.map((stat, i) => <span key={i} style={{ padding: "4px 10px", borderRadius: 6, fontSize: 11, fontWeight: 700, background: C.coralLight, color: C.coral }}>{stat.errorCategory}: {stat.errorType} ({stat.count})</span>) : <span style={{ color: C.textLight }}>No stats</span>}
                  </div>
                </RCard>
              </div>
              {errorLogs.errorLogs?.length > 0 && <RCard title="Recent Errors"><div style={{ overflowX: "auto", borderRadius: 10, border: `1px solid ${C.border}` }}><table style={tbl.table}><thead><tr>{["Category", "Type", "Reference", "Message", "Code", "Retries", "Last Occurred", ""].map(h => <th key={h} style={tbl.th}>{h}</th>)}</tr></thead><tbody>{errorLogs.errorLogs.map((error, i) => (
                <tr key={i} className="rpt-row">
                  <td style={tbl.td}><span style={{ padding: "3px 8px", borderRadius: 6, fontSize: 10, fontWeight: 600, background: C.bg, color: C.textMed }}>{error.errorCategory}</span></td>
                  <td style={tbl.td}><span style={{ padding: "3px 8px", borderRadius: 6, fontSize: 10, fontWeight: 600, background: C.orangeLight, color: C.orange }}>{error.errorType}</span></td>
                  <td style={{ ...tbl.td, fontSize: 11 }}>{error.referenceType}: {error.referenceId}</td>
                  <td style={{ ...tbl.td, maxWidth: 250, fontSize: 11 }}>{error.errorMessage}</td>
                  <td style={tbl.td}><span style={{ padding: "3px 8px", borderRadius: 6, fontSize: 10, fontWeight: 700, background: C.coralLight, color: C.coral }}>{error.errorCode}</span></td>
                  <td style={{ ...tbl.td, textAlign: "center", fontWeight: 700, color: error.retryCount > 0 ? C.coral : C.textLight }}>{error.retryCount}</td>
                  <td style={{ ...tbl.td, fontSize: 11 }}>{new Date(error.lastOccurredAt).toLocaleString()}</td>
                  <td style={tbl.td}><Eye size={15} style={{ cursor: "pointer", color: C.blue }} onClick={() => handleViewErrorDetails(error)} /></td>
                </tr>
              ))}</tbody></table></div></RCard>}
              {errorLogs.errorsByDate?.length > 0 && <RCard title="Errors by Date"><div style={{ overflowX: "auto", borderRadius: 10, border: `1px solid ${C.border}` }}><table style={tbl.table}><thead><tr>{["Date", "Category", "Type", "Count"].map(h => <th key={h} style={tbl.th}>{h}</th>)}</tr></thead><tbody>{errorLogs.errorsByDate.map((item, i) => <tr key={i} className="rpt-row"><td style={tbl.td}>{item.date}</td><td style={tbl.td}><span style={{ padding: "3px 8px", borderRadius: 6, fontSize: 10, fontWeight: 600, background: C.bg, color: C.textMed }}>{item.errorCategory}</span></td><td style={tbl.td}><span style={{ padding: "3px 8px", borderRadius: 6, fontSize: 10, fontWeight: 600, background: C.orangeLight, color: C.orange }}>{item.errorType}</span></td><td style={{ ...tbl.td, fontWeight: 700, color: C.coral }}>{item.count}</td></tr>)}</tbody></table></div></RCard>}
            </div>
          ) : <div style={{ textAlign: "center", padding: 40, color: C.textLight }}>Failed to load error logs</div>}
        </ModalBody>
        <ModalFooter style={{ gap: 8 }}>
          {errorLogs && errorLogs.totalPages > 1 && (
            <div style={{ flex: 1, display: "flex", alignItems: "center", gap: 8 }}>
              <button onClick={() => handleErrorLogsPageChange(errorLogsPage - 1)} disabled={errorLogsPage === 1 || isFetchingErrorLogs} style={{ padding: "6px 14px", borderRadius: 8, border: `1px solid ${C.border}`, background: C.white, fontSize: 12, fontWeight: 600, cursor: errorLogsPage === 1 ? "not-allowed" : "pointer" }}>Prev</button>
              <span style={{ fontSize: 12, color: C.textMed }}>Page {errorLogsPage} of {errorLogs.totalPages}</span>
              <button onClick={() => handleErrorLogsPageChange(errorLogsPage + 1)} disabled={errorLogsPage === errorLogs.totalPages || isFetchingErrorLogs} style={{ padding: "6px 14px", borderRadius: 8, border: `1px solid ${C.border}`, background: C.white, fontSize: 12, fontWeight: 600, cursor: errorLogsPage === errorLogs.totalPages ? "not-allowed" : "pointer" }}>Next</button>
            </div>
          )}
          <Button style={{ background: C.bg, border: `1px solid ${C.border}`, color: C.text, borderRadius: 10, fontWeight: 600, fontSize: 13 }} onClick={toggleErrorLogsModal}>Close</Button>
        </ModalFooter>
      </Modal>

      {/* ─── Error Details Modal ───────────────────────── */}
      <Modal isOpen={showErrorDetailsModal} toggle={toggleErrorDetailsModal} size="lg" centered>
        <ModalHeader toggle={toggleErrorDetailsModal}><span style={{ fontWeight: 700 }}>Error Details</span></ModalHeader>
        <ModalBody style={{ maxHeight: '70vh', overflowY: 'auto', fontFamily: "'Inter', sans-serif" }}>
          {selectedErrorForDetails ? (
            <div>
              <RCard>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, fontSize: 13 }}>
                  <div>
                    <p><strong>Category:</strong> <span style={{ padding: "3px 8px", borderRadius: 6, fontSize: 11, fontWeight: 600, background: C.bg, color: C.textMed }}>{selectedErrorForDetails.errorCategory}</span></p>
                    <p><strong>Type:</strong> <span style={{ padding: "3px 8px", borderRadius: 6, fontSize: 11, fontWeight: 600, background: C.orangeLight, color: C.orange }}>{selectedErrorForDetails.errorType}</span></p>
                    <p><strong>Code:</strong> <span style={{ padding: "3px 8px", borderRadius: 6, fontSize: 11, fontWeight: 700, background: C.coralLight, color: C.coral }}>{selectedErrorForDetails.errorCode}</span></p>
                  </div>
                  <div>
                    <p><strong>Reference:</strong> {selectedErrorForDetails.referenceType}: {selectedErrorForDetails.referenceId}</p>
                    <p><strong>Retries:</strong> <span style={{ fontWeight: 700, color: selectedErrorForDetails.retryCount > 0 ? C.coral : C.textLight }}>{selectedErrorForDetails.retryCount}</span></p>
                    <p><strong>First:</strong> {new Date(selectedErrorForDetails.firstOccurredAt).toLocaleString()}</p>
                    <p><strong>Last:</strong> {new Date(selectedErrorForDetails.lastOccurredAt).toLocaleString()}</p>
                  </div>
                </div>
                <div style={{ marginTop: 12, padding: 12, background: C.coralLight, borderRadius: 10, color: C.coral, fontSize: 13 }}><strong>Message:</strong> {selectedErrorForDetails.errorMessage}</div>
              </RCard>
              <RCard title="Error Details (JSON)">
                <pre style={{ background: C.bg, padding: 16, borderRadius: 10, maxHeight: 300, overflowY: "auto", fontSize: 11, border: `1px solid ${C.border}` }}>{JSON.stringify(selectedErrorForDetails.errorDetails, null, 2)}</pre>
              </RCard>
              {selectedErrorForDetails.metadata && Object.keys(selectedErrorForDetails.metadata).length > 0 && (
                <RCard title="Metadata (JSON)">
                  <pre style={{ background: C.bg, padding: 16, borderRadius: 10, maxHeight: 200, overflowY: "auto", fontSize: 11, border: `1px solid ${C.border}` }}>{JSON.stringify(selectedErrorForDetails.metadata, null, 2)}</pre>
                </RCard>
              )}
            </div>
          ) : <div style={{ textAlign: "center", padding: 40, color: C.textLight }}>No error details available</div>}
        </ModalBody>
        <ModalFooter><Button style={{ background: C.bg, border: `1px solid ${C.border}`, color: C.text, borderRadius: 10, fontWeight: 600, fontSize: 13 }} onClick={toggleErrorDetailsModal}>Close</Button></ModalFooter>
      </Modal>

      {/* ─── Network Diagnostics Modal ─────────────────── */}
      <Modal isOpen={showDiagnosticsModal} toggle={toggleDiagnosticsModal} size="lg" centered>
        <ModalHeader toggle={toggleDiagnosticsModal}><span style={{ fontWeight: 700 }}>Network Diagnostics</span></ModalHeader>
        <ModalBody style={{ maxHeight: '70vh', overflowY: 'auto', fontFamily: "'Inter', sans-serif" }}>
          {isFetchingDiagnostics ? <RLoader /> : networkDiagnostics?.devices ? (
            <div>
              <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
                <span style={{ padding: "4px 12px", borderRadius: 8, fontSize: 12, fontWeight: 600, background: C.blueLight, color: C.blue }}>POS IP: {networkDiagnostics.posIp}</span>
                <span style={{ padding: "4px 12px", borderRadius: 8, fontSize: 12, fontWeight: 600, background: C.bg, color: C.textMed }}>Subnet: {networkDiagnostics.posSubnet}.x</span>
              </div>
              {networkDiagnostics.devices.length > 0 ? (
                <div style={{ overflowX: "auto", borderRadius: 10, border: `1px solid ${C.border}` }}>
                  <table style={tbl.table}>
                    <thead><tr>{["Device", "IP", "Port", "Type", "Subnet", "Reachable", "Response", "Diagnosis"].map(h => <th key={h} style={tbl.th}>{h}</th>)}</tr></thead>
                    <tbody>{networkDiagnostics.devices.map((device, i) => {
                      const ms = typeof device.responseTimeMs === 'number' ? device.responseTimeMs : null;
                      const diagColor = device.diagnosis?.includes('Healthy') ? C.green : device.diagnosis?.includes('Slow') || device.diagnosis?.includes('may') ? C.orange : C.coral;
                      return (
                        <tr key={i} className="rpt-row">
                          <td style={{ ...tbl.td, fontWeight: 700 }}>{device.name}</td>
                          <td style={tbl.td}>{device.ip}</td>
                          <td style={tbl.td}>{device.port}</td>
                          <td style={tbl.td}><span style={{ padding: "3px 8px", borderRadius: 6, fontSize: 10, fontWeight: 600, background: device.type === 'printer' ? C.blueLight : C.greenLight, color: device.type === 'printer' ? C.blue : C.green }}>{device.type}</span></td>
                          <td style={tbl.td}><span style={{ color: device.subnetMatch ? C.green : C.coral, fontWeight: 700 }}>{device.subnetMatch ? 'Match' : 'Mismatch'}</span></td>
                          <td style={tbl.td}><span style={{ color: device.reachable ? C.green : C.coral, fontWeight: 700 }}>{device.reachable ? 'Yes' : 'No'}</span></td>
                          <td style={{ ...tbl.td, fontWeight: 700 }}>{ms != null ? `${ms}ms` : '-'}</td>
                          <td style={{ ...tbl.td, color: diagColor, fontWeight: 700 }}>{device.diagnosis}</td>
                        </tr>
                      );
                    })}</tbody>
                  </table>
                </div>
              ) : <div style={{ textAlign: "center", padding: 40, color: C.textLight }}>No devices found</div>}
            </div>
          ) : <div style={{ textAlign: "center", padding: 40, color: C.textLight }}>Error running diagnostics</div>}
        </ModalBody>
        <ModalFooter><Button style={{ background: C.bg, border: `1px solid ${C.border}`, color: C.text, borderRadius: 10, fontWeight: 600, fontSize: 13 }} onClick={toggleDiagnosticsModal}>Close</Button></ModalFooter>
      </Modal>
    </Fragment>
  );
};

export default Devices_list;
